/**
 * helper.js
 *
 * A collection of utility functions, React components, hooks, and constants for session management, data formatting, file downloads, UI elements, date calculations, validation, and more.
 *
 * Features:
 * - Session management for company and project
 * - Data formatting for dropdowns and selects
 * - File and CSV download utilities
 * - Date calculations and display components
 * - Loading spinners and overlays
 * - SweetAlert2 dialog helpers
 * - AbortController for HTTP request cancellation
 * - Validation utilities for required fields
 * - Language and image size constants
 * - Dynamic input list and timer components
 */

import { useState, useEffect } from 'react';
import * as Yup from 'yup'
import moment from "moment";
import { brandAll, buisnessAll, categoryAll, getKycCount, productAll, subBuisnessAll, subCatAll } from "../master/api";
import Swal from 'sweetalert2'
import { toAbsoluteUrl, getFullUrl } from '../../_metronic/helpers';

/**
 * Custom hook to get user permissions for a specific module.
 * Prefers per-module `accesses.modules_permissions[module].actions`.
 * Falls back to global Spatie `accesses.permissions` for backward compatibility.
 *
 * Action aliases (legacy Spatie ↔ new actions):
 * - write ↔ create
 * - edit ↔ edit
 * - download / upload / list / delete as-is
 *
 * @param {string} module - The module slug to check permissions for
 * @param {Object} user - User object containing access permissions
 * @returns {Array} - Array of permission / action strings for the module
 */
export const usePermission = (module, user) => {
    const globalPerms = (user?.accesses?.permissions || []).map((p) => String(p).toLowerCase())
    const modulePerms = module
        ? user?.accesses?.modules_permissions?.[module]
        : null

    let actions = []
    if (modulePerms && Array.isArray(modulePerms.actions) && modulePerms.actions.length > 0) {
        actions = modulePerms.actions.map((a) => String(a).toLowerCase())
    } else if (globalPerms.length > 0) {
        actions = [...globalPerms]
    } else {
        // Safe default when nothing is configured (matches previous behaviour)
        actions = ['write', 'create', 'edit', 'list', 'download', 'upload']
    }

    // Normalize aliases so existing UI checks keep working
    const normalized = new Set(actions)
    if (normalized.has('create')) normalized.add('write')
    if (normalized.has('write')) normalized.add('create')
    if (normalized.has('view')) normalized.add('list')
    if (normalized.has('list')) normalized.add('view')

    return Array.from(normalized)
}

/**
 * Validates if a string is a valid HTTP or HTTPS URL
 * @param {string} string - The string to validate as URL
 * @returns {boolean} - Returns true if valid URL, false otherwise
 */
export const isValidHttpUrl = (string) => {
    const pattern = /^(http|https):\/\/[^\s$.?#].[^\s]*$/i;
    return pattern.test(string);
}

/**
 * Redirects the browser to a specified URL
 * @param {string} url - The URL to redirect to
 */
export const Redirect = (url) => {
    window.location.href = url
}

/**
 * Retrieves company session data from localStorage
 * @returns {Object|null} - Company session object or null if not found/invalid
 */
export function getCompanySession() {
    const com_session = localStorage.getItem('com_session')
    if (com_session) {
        try {
            return JSON.parse(com_session)
        } catch (er) {
            console.error(er)
        }
    }
    return null
}

/**
 * @typedef {Object} ProjectSession
 * @property {number|string} id
 * @property {string} [project_name]
 * @property {string} [project_nick_name]
 * @property {string|null} [small_logo]
 * @property {string} [ref_key]
 * @property {string[]} [project_modules]
 */

/**
 * Retrieves project session data from localStorage
 * @returns {ProjectSession|null} - Project session object or null if not found/invalid
 */
export function getProjectSession() {
    const com_session = localStorage.getItem('pro_session')
    if (com_session) {
        try {
            return JSON.parse(com_session)
        } catch (er) {
            console.error(er)
        }
    }
    return null
}

/**
 * Keep only display fields for the company UI cache. Authorization stays on the token.
 * @param {Object} company
 * @returns {Object|null}
 */
export function toCompanySessionCache(company) {
    if (!company?.id) {
        return null
    }
    return {
        id: company.id,
        company_name: company.company_name ?? '',
        company_nick_name: company.company_nick_name ?? '',
        small_logo: company.small_logo ?? null,
        ref_key: company.ref_key ?? '',
    }
}

/**
 * Keep only display fields for the project UI cache. Authorization stays on the token.
 * @param {Object} project
 * @returns {Object|null}
 */
export function toProjectSessionCache(project) {
    if (!project?.id) {
        return null
    }
    return {
        id: project.id,
        project_name: project.project_name ?? '',
        project_nick_name: project.project_nick_name ?? '',
        small_logo: project.small_logo ?? null,
        ref_key: project.ref_key ?? '',
        project_modules: Array.isArray(project.project_modules)
            ? project.project_modules.map((slug) => String(slug))
            : [],
    }
}

/**
 * Role modules that are also saved on the project admin portal setting.
 * @param {string[]} roleModules
 * @param {string[]} projectModules
 * @returns {string[]}
 */
export function modulesAllowedInProject(roleModules, projectModules) {
    const allowed = new Set(
        (Array.isArray(projectModules) ? projectModules : []).map((slug) => String(slug))
    )
    const matched = (Array.isArray(roleModules) ? roleModules : [])
        .map((slug) => String(slug))
        .filter((slug) => allowed.has(slug))

    // Project Settings is always on, even when the role does not list this slug.
    if (!matched.includes('project_settings')) {
        matched.push('project_settings')
    }

    return matched
}

/**
 * Sets company session data in localStorage and removes project session
 * @param {Object} com - Company session object to store
 */
export function setCompanySession(com) {
    const payload = toCompanySessionCache(com)
    if (!payload) {
        return
    }
    localStorage.removeItem('pro_session')
    localStorage.setItem('com_session', JSON.stringify(payload))
}

/**
 * Sets project session data in localStorage and removes company session
 * @param {Object} com - Project session object to store
 */
export function setProjectSession(com) {
    const payload = toProjectSessionCache(com)
    if (!payload) {
        return
    }
    localStorage.removeItem('com_session')
    localStorage.setItem('pro_session', JSON.stringify(payload))
}

/**
 * Removes project session from localStorage and redirects to company list
 */
export function removeProjectSession() {
    localStorage.removeItem('pro_session')
    window.location.href = '/admin/company/list'
    // window.location.reload()
}

/**
 * Removes company session from localStorage and redirects to company list
 */
export function removeCompanySession() {
    localStorage.removeItem('com_session')
    window.location.href = '/admin/company/list'
    // window.location.reload()
}

/**
 * Checks for existing sessions and removes them, then redirects to company list
 */
export function checkAndRemoveSessions() {
    const comSession = getCompanySession();
    const proSession = getProjectSession();

    if (comSession || proSession) {
        if (comSession) {
            localStorage.removeItem('com_session')
        }
        if (proSession) {
            localStorage.removeItem('pro_session')
        }
        window.location.href = '/admin/company/list'; // Redirect after removing sessions
    }
}

/**
 * Formats brand data for Select2 dropdown component
 * @param {Array} data - Array of brand objects
 * @returns {Array} - Formatted array with value and label properties
 */
export const select2FormatBrand = (data) => {
    if (data.length > 0) {
        return data.map((brand) => {
            return { value: brand.id, label: brand.brand_name }
        })
    }
    return []
}

/**
 * React Select component wrapper
 * @param {Array} options - Array of options for the select component
 * @returns {JSX.Element} - Select component
 */
// const SelectComponent = (options) => (
//     <Select className="" options={options} />
// )

/**
 * Downloads data as a file (typically CSV)
 * @param {Object} params - Download parameters
 * @param {string} params.data - The data to download
 * @param {string} params.fileName - Name of the file to download
 * @param {string} params.fileType - MIME type of the file
 */
export const downloadCsv = ({ data, fileName, fileType }) => {
    // Create a blob with the data we want to download as a file
    const blob = new Blob([data], { type: fileType })
    // Create an anchor element and dispatch a click event on it
    // to trigger a download
    const a = document.createElement('a')
    a.download = fileName
    a.href = window.URL.createObjectURL(blob)
    const clickEvt = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
    })
    a.dispatchEvent(clickEvt)
    a.remove()
}

/**
 * Downloads a file from a given URL
 * @param {string} fileUrl - URL of the file to download
 * @param {string} filename - Name to save the file as
 */
export const downloadFileFromUrl = async (fileUrl, filename) => {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

/**
 * Calculates and displays the number of days between two dates
 * @param {Object} params - Date parameters
 * @param {string} params.fromDate - Start date
 * @param {string} params.toDate - End date
 * @returns {JSX.Element} - JSX element showing days difference
 */
export const DueDateInDays = ({ fromDate, toDate }) => {
    // Parse the dates using Moment.js
    const from = moment(fromDate);
    const to = moment(toDate);

    // Calculate the difference in days
    const differenceInDays = to.diff(from, 'days');

    return <><span className="bullet bullet-dot bg-success me-2"></span>Due in {differenceInDays} days</>
}

/**
 * Loading spinner component with custom styling
 * @returns {JSX.Element} - Loading spinner component
 */
export const Loader = () => {
    const styles = {
        borderRadius: '0.475rem',
        boxShadow: '0 0 50px 0 rgb(82 63 105 / 15%)',
        backgroundColor: '#fff',
        color: '#7e8299',
        fontWeight: '500',
        margin: '0',
        width: 'auto',
        padding: '1rem 2rem',
        top: 'calc(50% - 2rem)',
        //left: 'calc(50% - 4rem)',
    }
    return <div style={{ ...styles, position: 'absolute', textAlign: 'center' }}>
        <button className="btn btn-primary" type="button" disabled>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"> </span> Loading...
        </button>
    </div>
}

/**
 * Download spinner component
 * @param {Object} props - Component props
 * @param {string} props.className - CSS class name for the spinner
 * @returns {JSX.Element} - Spinner component
 */
export const Downloadloader = ({ className = 'spinner-border spinner-border' }) => {
    return <span className={className} role="status" aria-hidden="true" > </span >
}

/**
 * Document viewer button component that opens documents in a new tab
 * @param {Object} props - Component props
 * @param {Object} props.upload_file - File object to view
 * @param {string} props.className - CSS class name for the button
 * @returns {JSX.Element} - Download button component
 */
export const DownloadDocViwer = ({ upload_file, className = 'btn btn-sm btn-light-success' }) => {
    // const navigate = useNavigate()
    const handleNavigate = () => {

        const encodedState = encodeURIComponent(JSON.stringify(upload_file));
        const url = `/admin/complaint/doc_viewer?state=${encodedState}`;
        window.open(url, '_blank').focus();
    };
    return <button onClick={() => handleNavigate()} className={className}>
        <i className="fas fa-download"></i>
    </button>
}

/**
 * Custom hook to determine if the current viewport is web-sized
 * @param {number} threshold - Pixel threshold to determine web vs mobile (default: 768)
 * @returns {boolean} - True if viewport width is greater than threshold
 */
export const useIsWeb = (threshold = 768) => {
    const [isWeb, setIsWeb] = useState(window.innerWidth > threshold);

    useEffect(() => {
        const handleResize = () => {
            setIsWeb(window.innerWidth > threshold);
        };

        // Initial check on component mount
        handleResize();

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Cleanup on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [threshold]);

    return isWeb;
};

/**
 * Full-page loading overlay component
 * @returns {JSX.Element} - Full-page loader component
 */
export const FullPageLoader = () => {
    // CSS styles for the full-page loader
    const loaderStyles = {
        position: 'fixed', // Use 'fixed' to cover the entire screen
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 999, // Ensure it sits on top of other content
        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent background
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center' // Center the loader icon/text
    };

    return (
        <div style={loaderStyles}>
            {/* <div style={contentStyles}>
                Loading...
              
            </div> */}
            <Loader />
        </div>
    );
};

/**
 * Array of supported languages
 * @type {Array<string>}
 */
export const LangArray = [
    "Hindi",    // 0
    "Gujrati",  // 1
    "Marathi",  // 2
    "Kannada",  // 3
    "Malayalam",// 4 (Corrected spelling)
    "Tamil",    // 5
    "Telugu",   // 6
    "Punjabi",  // 7
    "Odia",     // 8
    "Assamese", // 9
    "Bengali",  // 10
    "English"   // 11
];

/**
 * Object containing size limits for different image types
 * @type {Object}
 */
export const SizeLimits = {
    small_logo: { width: 50, height: 50 },
    medium_logo: { width: 70, height: 70 },
    large_logo: { width: 100, height: 100 },
    small_banner: { width: 500, height: 300 },
    medium_banner: { width: 500, height: 300 },
    large_banner: { width: 500, height: 300 },
    // thumbnail_image: { width: 100, height: 100 },
    small_image: { width: 150, height: 150 },
    medium_image: { width: 500, height: 500 },
    slab_icon: { width: 160, height: 108 },
    category_icon: { width: 32, height: 32 },
    big_banner: { width: 1080, height: 360 },
};

/**
 * Timer component that counts down from a specified number of seconds
 * @param {Object} props - Component props
 * @param {number} props.second - Initial seconds to count down from
 * @param {boolean} props.active - Whether the timer should be active
 * @param {Function} props.removeActiveItemByIndex - Callback function when timer finishes
 * @returns {JSX.Element} - Timer component
 */
export const TimerSecond = ({ second, active, removeActiveItemByIndex }) => {
    const [timeRemaining, setTimeRemaining] = useState(second); // Initial time: 30 seconds
    const [timerFinished, setTimerFinished] = useState(false); // To track if the timer is finished
    const [isRunning, setIsRunning] = useState(false); // To track if the timer is running

    // Function to start the timer when the button is clicked
    const startTimer = () => {
        setIsRunning(true); // Mark the timer as running
        setTimerFinished(false); // Reset timer finished state
        setTimeRemaining(second); // Reset the timer to 30 seconds
    };

    useEffect(() => {
        if (active) {
            startTimer()
        }
    }, [])

    useEffect(() => {
        if (!isRunning) return; // Don't start the timer if it's not running

        const timerInterval = setInterval(() => {
            setTimeRemaining((prevTime) => {
                if (prevTime > 1) {
                    return prevTime - 1; // Decrease time by 1 second
                } else {
                    clearInterval(timerInterval); // Stop the interval when time reaches 0
                    setTimerFinished(true); // Mark timer as finished
                    removeActiveItemByIndex(active);
                    return 0;
                }
            });
        }, 1000); // Update every 1 second (1000ms)

        // Cleanup the interval when the component unmounts or timer stops
        return () => clearInterval(timerInterval);
    }, [isRunning]); // Dependency on isRunning to start/stop the timer

    return (
        <> {!timerFinished && (
            <>
                <span className="text-danger fw-folder">Please wait: <span className="text-dark">{timeRemaining} </span>seconds</span>
            </>
        )}
        </>
    );
};

/**
 * Dynamic input list component that fetches and filters data based on type
 * @param {Object} props - Component props
 * @param {string} props.type - Type of data to fetch (business_segment, category, brand, etc.)
 * @param {string} props.input - Input value to filter the data
 * @param {string|number} props.id - Optional ID for dependent data fetching
 * @returns {JSX.Element} - Filtered dropdown list component
 */
export const InputList = ({ type, input, id }) => {
    const [data, setData] = useState([])
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let fetchFunction;
                // Determine the fetch function based on the `type` prop
                switch (type) {
                    case "business_segment":
                        fetchFunction = buisnessAll;
                        break;
                    case "category":
                        fetchFunction = categoryAll;
                        break;
                    case "brand":
                        fetchFunction = brandAll;
                        break;
                    case "product":
                        fetchFunction = productAll;
                        break;
                    case "sub_business_segment":
                        fetchFunction = subBuisnessAll;
                        break;
                    case "sub_category":
                        fetchFunction = subCatAll;
                        break;
                    default:
                        throw new Error("Invalid type provided");
                }
                let result = null;
                if (id) {
                    if (type === 'sub_business_segment') {
                        result = await fetchFunction({ business_segment_id: id });
                    }
                    if (type === 'sub_category') {
                        result = await fetchFunction({ category_id: id });
                    }
                } else {
                    result = await fetchFunction();
                }
                setData(result);
            } catch (error) {
                console.error(`Error fetching data for type ${type}:`, error);
            }
        };
        fetchData();
    }, []);

    // Filter the data based on the input value
    useEffect(() => {
        const lowerCaseInput = input.toLowerCase(); // Case-insensitive search
        const filtered = data.filter((item) =>
            item.label.toLowerCase().includes(lowerCaseInput)
        );
        setFilteredData(filtered);
    }, [input, data]);

    return (
        (filteredData?.length > 0 && input) && (
            <div
                className="card border mt-2 p-2 dropdown-list"
                style={{ position: "absolute", zIndex: 1, width: "97%" }}
            >
                <div
                    className="dropdown-scroll"
                    style={{ maxHeight: "200px", overflowY: "scroll" }}
                >
                    {filteredData.map((item) => (
                        <div
                            className="fs-6 text-capitalize text-black p-1 fw-semibold hover-overlay  dropdown-item"
                            key={item.id || item.label} // Use a unique identifier if available
                        >
                            {item.label}
                        </div>
                    ))}
                </div>
            </div>
        )
    );

};

/**
 * Gets today's date and date from 7 days ago in YYYY-MM-DD format
 * @returns {Object} - Object containing today and pastSevenDays dates
 */
export const getDates = () => {
    const today = moment().format('YYYY-MM-DD');
    const pastSevenDays = moment().subtract(7, 'days').format('YYYY-MM-DD');
    return { today, pastSevenDays };
};

/**
 * Shows a SweetAlert2 dialog for adding remarks
 * @returns {Promise} - Promise that resolves with the user input
 */
export const swalRemarksAlert = async () => {
    return await Swal.fire({
        title: 'Add Your Remarks',
        input: 'textarea',
        inputPlaceholder: 'Add your remarks ...',
        inputAttributes: {
            id: 'remarksTextarea',
            required: true
        },
        showCancelButton: true,
        confirmButtonText: 'Update',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        width: '500px',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary'
        },
        preConfirm: (value) => {
            if (!value || !value.trim()) {
                Swal.showValidationMessage('Please fill the remarks');
            }
            return value;
        }
    });
};

/**
 * Shared AbortController instance for cancelling HTTP requests
 * @type {AbortController|null}
 */
let sharedAbortController = null

/**
 * Cancels the previous HTTP request and creates a new AbortController
 * @returns {AbortSignal} - New abort signal for the next request
 */
export const cancelPreviousRequest = () => {
    if (sharedAbortController) {
        sharedAbortController.abort()
    }
    sharedAbortController = new AbortController()
    return sharedAbortController.signal
}

/**
 * Validates if all required fields are present in the data object
 * @param {Object} data - The data object to validate
 * @param {Array} requiredFields - Array of required field names
 * @returns {boolean} - Returns true if all required fields are present, false otherwise
 */
export const validateRequiredFields = (data, requiredFields) => {
    // Check if data is an object and not null
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false;
    }

    // Check if requiredFields is an array
    if (!Array.isArray(requiredFields)) {
        return false;
    }

    // If no required fields specified, return true
    if (requiredFields.length === 0) {
        return true;
    }

    // Check if all required fields exist in data and have non-empty values
    return requiredFields.every(field => {
        const value = data[field];

        if (Array.isArray(value)) {
            // Must have at least one element
            return value.length > 0;
        }

        if (typeof value === 'object' && value !== null) {
            // Object must not be empty
            return Object.keys(value).length > 0;
        }

        // For primitive values: must not be undefined, null, or empty string
        return value !== undefined && value !== null && value !== '';
    });
};

/**
 * SmallTextComponent - A reusable component for displaying small, muted text with optional text replacement.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.text="After saving the Basic Info, you can make further edits to :type."] - The text to display. Can include `:type` as a placeholder for dynamic content.
 * @param {string} [props.customClass=""] - Additional CSS classes to apply to the text element.
 * @param {string} [props.replacer=""] - The text to replace the `:type` placeholder in the text prop.
 * @returns {JSX.Element} A small, muted text element with optional dynamic content.
 * 
 * @example
 * // Basic usage
 * <SmallTextComponent />
 * 
 * @example
 * // With custom text and replacement
 * <SmallTextComponent 
 *   text="Please update the :type section next."
 *   replacer="profile"
 *   customClass="mt-2"
 * />
 */
export const SmallTextComponent = (props) => {
    const { text = 'After saving the Basic Info, you can make further edits to :type.', customClass = '', replacer = '' } = props;

    return (
        (text) && (
            <small className={`text-muted d-block ms-1 ${customClass}`}>
                {`(${text.replace(':type', replacer)})`}
            </small>
        )
    )
}

/**
 * A reusable component for image upload with preview and remove functionality.
 * Integrates with Formik for form state management and supports image validation.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {string} [props.boxSizeClass='w-150px h-150px'] - classes for image box size.
 * @param {string} [props.image=''] - URL of the currently displayed image or preview
 * @param {string} [props.name='small_logo'] - Name attribute for the file input and form field
 * @param {string} [props.accept='.png, .jpg, .jpeg'] - Comma-separated list of accepted file types
 * @param {string} [props.error=''] - Error message to display below the component
 * @param {string} [props.label=''] - Display label for the upload area
 * @param {string} [props.sizeType=null] - Type of image (e.g., 'small_logo', 'banner')
 * @param {Object} props.formik - Formik form context for state management
 * @param {string} [props.previewField=''] - Formik field name for storing preview URL
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {string} [props.description=null] - Type of text
 * @returns {JSX.Element} Rendered image upload component with preview and controls
 * 
 * @example
 * <IconViewerComponent
 *   name="logo"
 *   label="Company Logo"
 *   formik={formik}
 *   previewField="logoPreview"
 *   required={true}
 * />
 */
export const IconViewerComponent = (props) => {
    const {
        boxSizeClass = 'w-150px h-150px',
        image = '',
        name = "small_logo",
        accept = ".png, .jpg, .jpeg",
        error = '',
        label = '',
        sizeType = null,
        formik = null,
        previewField = '',
        required = false,
        description = null
    } = props;

    const blankImg = '/media/logos/blank-image.svg';

    /**
     * Handles the remove button click event
     * @param {Event} e - The click event
     */
    const handleRemove = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Create a proper synthetic event that matches what Formik expects
        const event = {
            target: {
                name: name,
                value: '',
                files: [null]
            },
            currentTarget: {
                name: name,
                files: [null]
            }
        };

        // Call the uploader with the synthetic event
        ImageUploadViewer(formik, event, name, sizeType, previewField);

        if (formik && typeof formik !== 'undefined') {
            formik.setFieldValue(name, null);
            if (previewField) formik.setFieldValue(previewField, '');
        }
    };

    return (
        <>
            <div className='d-flex align-items-center'>
                <div className="image-input image-input-empty image-input-outline mb-3" style={{ backgroundImage: `url(${toAbsoluteUrl(getFullUrl(image) || blankImg)})` }}>
                    <div className={`image-input-wrapper ${boxSizeClass}`}></div>
                    <label className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="change" data-bs-toggle="tooltip" title="" data-bs-original-title="Change avatar">
                        <i className="bi bi-pencil-fill fs-7"></i>
                        <input
                            onChange={(event) => ImageUploadViewer(formik, event, name, sizeType, previewField)}
                            type="file"
                            name={name}
                            accept={accept}
                        />
                    </label>
                    <span
                        className={`btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow${((image && typeof image === 'string' && !image.includes('assets/images/blank-image.svg')) || (error && !error.includes('required'))) ? ' d-block' : ''}`}
                        data-kt-image-input-action="remove"
                        data-bs-toggle="tooltip"
                        title=""
                        data-bs-original-title="Remove avatar"
                        onClick={handleRemove}
                    >
                        <i className="bi bi-x fs-2"></i>
                    </span>
                </div>
                {description ? (
                    <div className="text-mute px-4 fs-7">
                        <h5 className={`col-form-label${(!label && required) ? ' required' : ''} fw-semibold fs-6`}>{description}</h5>
                    </div>
                ) : null}
            </div>
            <div className="text-dark px-4 fs-7">
                {(label) ? (
                    <h5 className={`text-gray-700 w-bolder${(required) ? ' required' : ''} mb-0`}>{label}</h5>
                ) : null}
                <div className='text-danger'>
                    {error}
                </div>
            </div>
        </>
    )
}

/**
 * Validates the dimensions of an image file.
 * 
 * @param {File|string} file - The image file to validate. If a string is provided (e.g., existing URL), validation is skipped.
 * @param {number} requiredWidth - The maximum width the image is allowed to have.
 * @param {number} requiredHeight - The maximum height the image is allowed to have.
 * @returns {Promise<boolean>} A promise that resolves to `true` if the image meets the dimension constraints, otherwise `false`.
 * 
 * Notes:
 * - Currently, the function checks if the image width and height are **less than** the specified dimensions.
 * - If `file` is null, undefined, or a string, it assumes validation is not required and resolves `true`.
 */
export const validateImageDimensions = async (file, requiredWidth, requiredHeight) => {
    return new Promise((resolve) => {
        if (!file || typeof file === "string") {
            return resolve(true); // Skip validation for existing images
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const isValid = img.width === requiredWidth && img.height === requiredHeight;
                // const isValid = img.width > requiredWidth && img.height > requiredHeight;

                resolve(isValid);
            };
            img.src = e.target.result;
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Handles image file upload, validation, and preview generation for form fields.
 * Validates image dimensions against predefined size limits and updates form state.
 *
 * @async
 * @param {Object} formik - Formik form context for state management
 * @param {Event} event - File input change event
 * @param {string} type - The form field name for the uploaded file
 * @param {string} [typeLogo=null] - Optional override for size limit lookup
 * @param {string} [previewField=''] - Formik field name for storing preview URL
 * @returns {Promise<void>}
 * 
 * @example
 * // In your form component:
 * <input 
 *   type="file"
 *   onChange={(e) => ImageUploadViewer(formik, e, 'logo', 'small_logo', 'logoPreview')}
 * />
 */
export const ImageUploadViewer = async (formik, event, type, typeLogo = null, previewField = '') => {
    const file = event?.currentTarget?.files[0];
    // Update form field with selected file
    formik.setFieldValue(type, file);
    if (!file) return;

    // Get size constraints for this image type
    let _type = typeLogo ? typeLogo : type;
    const size = SizeLimits[_type];
    // Validate dimensions if size constraints exist
    if (size) {
        const isValid = await validateImageDimensions(file, size.width, size.height);
        if (!isValid) {
            formik.setFieldValue(type, '');
            if (previewField) {
                formik.setFieldValue(previewField, '');
            }
            formik.setFieldError(
                type,
                `Image dimensions should be ${size.width}x${size.height} pixels.`
            );
            return;
        } else {
            if (previewField) {
                // Generate and display image preview
                const reader = new FileReader();
                reader.onloadend = () => {
                    formik.setFieldValue(previewField, reader.result);
                };
                reader.readAsDataURL(file);
            }
        }
    }
};

/**
 * Creates a Yup validation schema for image fields with type and dimension validation
 * @param {string} type - The type of image field (e.g., 'small_logo', 'banner_image1')
 * @returns {Yup.MixedSchema} Configured Yup validation schema for image fields
 */
export const imageFieldValidator = (type) => {
    return Yup.mixed()
        // Validates if the value is either a string (existing URL) or a File object
        .test('file-or-string', 'Invalid file type', (value) => {
            if (!value) return true; // Skip validation if no value
            if (typeof value === 'string') return true; // Accept existing URLs
            return value instanceof File; // Must be a File object for new uploads
        })
        // Validates image dimensions against SizeLimits configuration
        .test("file-dimensions", async function (value) {
            if (!value || typeof value === "string") return true; // Skip if no file or already validated URL

            const size = SizeLimits[type];
            if (!size) return true; // Skip if no size limit defined for this type

            // Validate image dimensions using helper function
            const isValid = await validateImageDimensions(value, size.width, size.height);
            if (!isValid) {
                return this.createError({
                    message: `Image dimensions should be ${size.width}x${size.height} pixels.`,
                });
            }

            return true;
        });
};

/**
 * Generates a Yup validation schema for a form field based on its configuration.
 *
 * The function maps a field's `type` and related validation options (such as
 * `min`, `max`, `required`, and `accept`) to the appropriate Yup schema.
 * It supports common form field types including numbers, dates, checkboxes,
 * arrays, files, and text-based inputs.
 *
 * @param {Object} config - Field configuration object
 *
 * @returns {Yup.Schema} A Yup validation schema corresponding to the field configuration
 */
export const getYupFieldType = (config) => {
    let schema;

    switch (config.type) {
        case 'textarea':
            schema = Yup.string();
            if (config.min !== undefined) {
                schema = schema.test(
                    'min-text-length',
                    `${config.label} must be at least ${config.min} alphabets.`,
                    (value) => {
                        if (!value) return false;

                        const textOnly = value
                            .replace(/<[^>]*>/g, '')
                            .replace(/&nbsp;/g, ' ')
                            .trim();

                        return textOnly.length >= config.min;
                    }
                );
            }
            if (config.max !== undefined) {
                schema = schema.schema = schema.test(
                    'max-text-length',
                    `${config.label} must not exceed ${config.max} alphabets.`,
                    (value) => {
                        if (!value) return false;

                        const textOnly = value
                            .replace(/<[^>]*>/g, '')
                            .replace(/&nbsp;/g, ' ')
                            .trim();

                        return textOnly.length <= config.max;
                    }
                );
            }
            break;
        case 'number':
            schema = Yup.number()
                .typeError(`${config.label} must be a number`);
            if (config.min !== undefined) {
                schema = schema.min(config.min, `${config.label} must be at least ${config.min}`);
            }
            if (config.max !== undefined) {
                schema = schema.max(config.max, `${config.label} must be at most ${config.max}`);
            }
            break;
        case 'date':
            schema = Yup.date()
                .typeError(`${config.label} must be a valid date`);
            break;
        case 'checkbox':
            schema = Yup.boolean();
            break;
        case 'multiselect':
            schema = Yup.array().of(Yup.string());
            break;
        case 'file':
            schema = Yup.mixed().test(
                'fileType',
                'Unsupported file format',
                function (value) {
                    if (!value) return true;
                    if (typeof value === 'string') return true; // Accept existing URLs
                    // const allowedTypes = config.accept?.split(',');
                    // return allowedTypes?.includes(value.type);
                    return true
                }
            );
            break;
        case 'array':
            schema = Yup.array();
            if (config.required) {
                schema = schema.min(1, `${config.label} is required`);
            }
            break;
        default:
            // text, radio, select, unknown types
            schema = Yup.string();
            if (config.min !== undefined) {
                schema = schema.min(config.min, `${config.label} must be at least ${config.min} alphabets.`);
            }
            if (config.max !== undefined) {
                schema = schema.max(config.max, `${config.label} must not exceed ${config.max} alphabets.`);
            }
    }

    return schema;
};


/**
 * Determines the file type based on the URL's file extension
 * @param {string} url - The URL or file path to check
 * @returns {string|null} - Returns the file type as 'image', 'pdf', 'document', or 'csv'.
 *                        Returns null if the file type is not recognized.
 * @example
 * checkFileType('document.pdf') // returns 'pdf'
 * checkFileType('image.jpg')    // returns 'image'
 * checkFileType('data.xlsx')    // returns 'document'
 */
export const checkFileType = (url) => {
    // Get file extension from URL, handling query parameters and hashes
    const ext = url.split('.').pop().split(/\#|\?/)[0].toLowerCase();

    // Define file type categories with their respective extensions
    const imageExt = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const pdfExt = ['pdf'];
    const docExt = ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'];
    const csvExt = ['csv'];

    // Check and return the corresponding file type
    if (imageExt.includes(ext)) return 'image';
    if (pdfExt.includes(ext)) return 'pdf';
    if (docExt.includes(ext)) return 'document';
    if (csvExt.includes(ext)) return 'csv';

    // Return null for unrecognized file types
    return null;
}






const HeaderSectionCount = () => {
    const [count, setCount] = useState({
        totalUploaded: 0,
        totalProccesed: 0,
        totalUnProccesed: 0
    })
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const fetchCounts = async () => {
            try {
                await new Promise(res => setTimeout(res, 1000))
                const res = await getKycCount()
                setCount(res.data)
            } catch (err) {
                console.error(err)
            } finally {
                setIsLoading(false)
            }
        }

        fetchCounts()
    }, [])
    return (
        <div className='d-flex'>
            <span className="badge badge-light-danger p-5 me-4 fs-6 text-dark fw-semibold">
                Total Uploaded :
                {isLoading ? (
                    <span className="spinner-grow spinner-grow-sm text-primary ms-2" />
                ) : (
                    <span className='text-dark fw-semibold ms-2'>
                        {count?.totalUploaded}
                    </span>
                )}
            </span>

            <span className="badge badge-light-success p-5 me-4 fs-6 text-dark fw-semibold">
                Processed :
                {isLoading ? (
                    <span className="spinner-grow spinner-grow-sm text-primary ms-2" />
                ) : (
                    <span className='text-dark fw-semibold ms-2'>
                        {count?.totalProccesed}
                    </span>
                )}
            </span>

            <span className="badge badge-light p-5 me-4 fs-6 text-dark fw-semibold">
                Un Processed :
                {isLoading ? (
                    <span className="spinner-grow spinner-grow-sm text-primary ms-2" />
                ) : (
                    <span className='text-dark fw-semibold ms-2'>
                        {count?.totalUnProccesed}
                    </span>
                )}
            </span>
        </div>
    )
}

export default HeaderSectionCount